import {useState} from 'react';
import {useForm} from 'react-hook-form';
import {Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody} from '@/components/ui/dialog.tsx';
import {Button} from '@/components/ui/button.tsx';
import {Input} from '@/components/ui/input.tsx';
import {Label} from '@/components/ui/label.tsx';
import {Spinner} from '@/components/ui/spinner.tsx';
import {toast} from 'sonner';
import {CustomerDetails, CustomerDetailsUpdateForm, useUpdateCustomerDetailsMutation} from '@/store/customerFile.ts';

interface Props {
    open: boolean;
    onClose: () => void;
    customer: CustomerDetails;
}

export function EditCustomerDetailsModal({open, onClose, customer}: Props) {
    const [update, {isLoading}] = useUpdateCustomerDetailsMutation();
    const {register, handleSubmit, formState: {errors}} = useForm<CustomerDetailsUpdateForm>({
        defaultValues: {
            business_name: customer.business_name,
            receipt_name: customer.receipt_name ?? '',
            receipt_email: customer.receipt_email ?? '',
            contact_name: customer.contact_name ?? '',
            email: customer.email ?? '',
            phone: customer.phone ?? '',
            phone2: customer.phone2 ?? '',
            fax: customer.fax ?? '',
        }
    });

    async function onSubmit(data: CustomerDetailsUpdateForm) {
        try {
            await update(data).unwrap();
            toast.success('פרטי הלקוח עודכנו בהצלחה');
            onClose();
        } catch {
            toast.error('שגיאה בעדכון הפרטים');
        }
    }

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>עריכת פרטי תיק לקוח</DialogTitle>
                </DialogHeader>
                <DialogBody>
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 pt-2">
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                            <Label>שם הארגון *</Label>
                            <Input {...register('business_name', {required: 'שדה חובה'})} />
                            {errors.business_name && (
                                <small className="text-red-500">{errors.business_name.message}</small>
                            )}
                        </div>
                        <div className="space-y-1.5">
                            <Label>שם לקבלה</Label>
                            <Input {...register('receipt_name')} />
                        </div>
                        <div className="space-y-1.5">
                            <Label>שם איש קשר</Label>
                            <Input {...register('contact_name')} />
                        </div>
                        <div className="space-y-1.5">
                            <Label>מייל</Label>
                            <Input type="email" {...register('email')} dir="ltr" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>מייל לקבלה</Label>
                            <Input type="email" {...register('receipt_email')} dir="ltr" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>טלפון</Label>
                            <Input type="tel" {...register('phone')} dir="ltr" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>טלפון 2</Label>
                            <Input type="tel" {...register('phone2')} dir="ltr" />
                        </div>
                        <div className="space-y-1.5">
                            <Label>פקס</Label>
                            <Input type="tel" {...register('fax')} dir="ltr" />
                        </div>
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                        <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>
                            ביטול
                        </Button>
                        <Button type="submit" disabled={isLoading}>
                            {isLoading && <Spinner size="sm" />}
                            שמור שינויים
                        </Button>
                    </div>
                </form>
                </DialogBody>
            </DialogContent>
        </Dialog>
    );
}
